import React from 'react';
import { AlertTriangleIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Card } from '../ui/Card';
import { Toggle } from '../ui/Toggle';
import type { AgeRule, LeadSource } from '../../types';
import { findOverlaps, ruleSummary } from '../../utils/format';
interface CadenceEditorProps {
  source: LeadSource;
  onChange: (rules: AgeRule[]) => void;
}
const numberInput = 'tabular h-10 w-16 rounded-lg border border-ink-200 bg-white text-center text-sm font-medium text-ink-900 outline-none transition-colors focus:border-brand-400 focus:ring-2 focus:ring-brand-100';
export function CadenceEditor({
  source,
  onChange
}: CadenceEditorProps) {
  const overlaps = findOverlaps(source.rules);
  const update = (id: string, patch: Partial<AgeRule>) => {
    onChange(source.rules.map(rule => rule.id === id ? {
      ...rule,
      ...patch
    } : rule));
  };
  const addRule = () => {
    const last = source.rules[source.rules.length - 1];
    const start = last ? last.maxDays + 1 : 0;
    onChange([...source.rules, {
      id: `${source.id}-${Date.now()}`,
      minDays: start,
      maxDays: start + 3,
      callsPerDay: 1,
      enabled: true
    }]);
  };
  return <Card className="overflow-hidden" data-id="element-21" data-mp="24k74wzehr8gi">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 px-6 py-4" data-id="element-22" data-mp="1zlmaxhpu1l0i">
        <div data-id="element-23" data-mp="1bvrz93h922hu">
          <h2 className="text-sm font-semibold text-ink-900" data-id="element-24" data-mp="1bv9ypunk1244">{source.name} — Call Cadence</h2>
          <p className="mt-0.5 text-xs text-ink-500" data-id="element-25" data-mp="16xgpstuv9u05">
            Required calls per day, by how old the lead is
          </p>
        </div>
        <button type="button" onClick={addRule} className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-ink-300 hover:bg-ink-50" data-id="element-26" data-mp="1ln5w13htgo7a">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-27" data-mp="o6yzsx1f5cih" />
          Add age range
        </button>
      </div>

      {overlaps.size > 0 ? <div className="flex items-start gap-2.5 border-b border-amber-200 bg-amber-50 px-6 py-3" data-id="element-28" data-mp="2o8gi3kxvo1gf">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" data-id="element-29" data-mp="2t5yu5k76ywsn" />
          <p className="text-xs text-amber-900" data-id="element-30" data-mp="2xzbdbet3qbe0">
            Overlapping age ranges detected. When ranges overlap, the first matching rule is applied
            — adjust the highlighted rows.
          </p>
        </div> : null}

      <ul className="divide-y divide-ink-200" data-id="element-31" data-mp="2a8ntbv5wvvb3">
        {source.rules.map(rule => {
        const overlapping = overlaps.has(rule.id);
        return <li key={rule.id} className={twMerge('px-6 py-4 transition-colors', overlapping ? 'bg-amber-50/60' : rule.enabled ? '' : 'bg-ink-50/60')} data-id="element-32" data-mp="2e97r90yb72ya">
              <div className="flex flex-wrap items-center gap-3" data-id="element-33" data-mp="3uoits8ev5ub2">
                <div className="flex items-center gap-2" data-id="element-34" data-mp="3uqvhl27ayyw1">
                  <label className="text-xs font-medium text-ink-500" htmlFor={`${rule.id}-min`} data-id="element-35" data-mp="8ma1pqxgx6r3">
                    Age
                  </label>
                  <input id={`${rule.id}-min`} type="number" min={0} value={rule.minDays} onChange={e => update(rule.id, {
                minDays: Number(e.target.value)
              })} className={numberInput} data-id="element-36" data-mp="3qkz1eq5j9qk" />
                  <span className="text-xs text-ink-500" data-id="element-37" data-mp="3h2lnfrwfw6s7">to</span>
                  <input aria-label="Maximum age in days" type="number" min={0} value={rule.maxDays} onChange={e => update(rule.id, {
                maxDays: Number(e.target.value)
              })} className={numberInput} data-id="element-38" data-mp="3h57qpfcas04m" />
                  <span className="text-xs font-medium text-ink-500" data-id="element-39" data-mp="16ak6uesjwamf">days</span>
                </div>

                <span className="text-ink-300" aria-hidden="true" data-id="element-40" data-mp="1ddrygo5ih8k0">
                  →
                </span>

                <div className="flex items-center gap-2" data-id="element-41" data-mp="1icbp31v1v8ip">
                  <input aria-label="Required calls per day" type="number" min={0} max={12} value={rule.callsPerDay} onChange={e => update(rule.id, {
                callsPerDay: Number(e.target.value)
              })} className={numberInput} data-id="element-42" data-mp="14dlrzm88d8uc" />
                  <span className="text-xs font-medium text-ink-500" data-id="element-43" data-mp="zftpxzdvg6cf">calls / day</span>
                </div>

                <div className="ml-auto flex items-center gap-3" data-id="element-44" data-mp="knjin2yg708l">
                  <Toggle checked={rule.enabled} onChange={checked => update(rule.id, {
                enabled: checked
              })} label={`${rule.enabled ? 'Disable' : 'Enable'} rule`} data-id="element-45" data-mp="pph8wh59mvb1" />
                  <button type="button" onClick={() => onChange(source.rules.filter(r => r.id !== rule.id))} className="rounded-lg p-2 text-ink-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label="Delete age range" data-id="element-46" data-mp="3t7lqghzhwx20">
                    <Trash2Icon className="h-4 w-4" data-id="element-47" data-mp="212f91pqz536" />
                  </button>
                </div>
              </div>
              <p className={twMerge('mt-2.5 text-xs leading-relaxed', rule.enabled ? 'text-ink-500' : 'text-ink-400 line-through')} data-id="element-48" data-mp="fzioazb317oh">
                {ruleSummary(source.name, rule)}
              </p>
            </li>;
      })}
      </ul>
    </Card>;
}